/**
 * Small shared visuals for the Logic Decomposer: disposition pill, complexity bar, status
 * pill. Kept in one place so the palette (handoff §2, locked) is applied once.
 */

import {
  color, font, disposition as dispColor, complexity as complexColor, status as statusBg,
  outcomeColor, OUTCOME_LABEL,
} from '../tokens';
import type { Disposition, Complexity, ArtifactStatus, Outcome } from '../tokens';

/**
 * The single consultant-facing disposition pill — renders the six-outcome taxonomy
 * (Box 1-4 / Consolidate / Decommission / Queue), the same words Framework Mapping uses.
 * (Task 1218070214136694, subtask "One consultant-facing disposition taxonomy".)
 */
export function OutcomePill({ value, confidence, detail }: { value: Outcome; confidence?: number; detail?: string }) {
  const c = outcomeColor[value];
  return (
    <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 6 }}>
      <span
        style={{
          background: `${c.bg}1a`, color: c.fg, border: `1px solid ${c.bg}40`,
          padding: '3px 9px', borderRadius: 8, fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap',
        }}
      >
        {OUTCOME_LABEL[value]}
        {confidence != null && (
          <span style={{ fontWeight: 600, opacity: 0.75 }}> · {Math.round(confidence * 100)}%</span>
        )}
      </span>
      {detail && <span style={{ fontSize: 12, color: color.textMuted }}>{detail}</span>}
    </span>
  );
}

/**
 * LEGACY 4-box pill (Migrate / Document / …). Retained only for the dead old FrameworkMapping
 * view and any export-alias surface. New UI uses OutcomePill above.
 */
export function DispositionPill({ value, confidence, detail }: { value: Disposition; confidence?: number; detail?: string }) {
  const c = dispColor[value];
  return (
    <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 6 }}>
      <span
        style={{
          background: `${c.bg}1a`, color: c.fg, border: `1px solid ${c.bg}40`,
          padding: '3px 9px', borderRadius: 8, fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap',
        }}
      >
        {value}
        {confidence != null && (
          <span style={{ fontWeight: 600, opacity: 0.75 }}> · {Math.round(confidence * 100)}%</span>
        )}
      </span>
      {detail && <span style={{ fontSize: 12, color: color.textMuted }}>{detail}</span>}
    </span>
  );
}

export function ComplexityBar({ value, pct }: { value: Complexity; pct: number }) {
  const c = complexColor[value];
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 120 }}>
      <div style={{ flex: 1, height: 5, background: '#e9eaf3', borderRadius: 999, overflow: 'hidden' }}>
        <div style={{ width: `${Math.max(0, Math.min(100, pct))}%`, height: '100%', background: c, borderRadius: 999 }} />
      </div>
      <span style={{ fontSize: 12, fontWeight: 600, color: c, minWidth: 46 }}>{value}</span>
    </div>
  );
}

export function StatusPill({ value }: { value: ArtifactStatus }) {
  return (
    <span
      style={{
        background: statusBg[value], color: color.ink, padding: '3px 9px', borderRadius: 8,
        fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap',
      }}
    >
      {value}
    </span>
  );
}

export function CatChip({ cat }: { cat: 1 | 2 | 3 }) {
  return (
    <span
      style={{
        border: `1px solid ${color.cardBorder}`, background: '#fbfbfe', color: color.textMuted,
        padding: '2px 7px', borderRadius: 6, fontSize: 11, fontWeight: 700, fontFamily: font.ui,
      }}
    >
      Cat {cat}
    </span>
  );
}
